<template>
  <div>
    <Head :title="__('Signaler un problème')" />
    
    <div class="max-w-3xl mx-auto">
      <div class="flex items-center space-x-3 mb-6">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8 text-indigo-600">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        <h1 class="text-2xl font-bold text-gray-900">{{ __('Signaler un problème') }}</h1>
      </div>

      <!-- Support Contact Card (Phone Number) -->
      <div class="mb-6 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div class="flex items-start space-x-3">
          <div class="p-2 bg-primary rounded-lg text-white shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-2.824-1.806-5.18-4.156-6.983-6.982l1.293-.97c.363-.271.527-.834.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
            </svg>
          </div>
          <div>
            <h3 class="text-sm font-bold text-slate-900">{{ __('Centre d\'appel & Assistance directe') }}</h3>
            <p class="text-xs text-slate-600 mt-0.5">{{ __('Besoin d\'une aide immédiate ? Notre équipe est également disponible par téléphone.') }}</p>
          </div>
        </div>
        <div class="shrink-0 flex items-center">
          <a :href="'tel:' + supportPhone" class="inline-flex items-center space-x-2 bg-primary hover:bg-hover text-white font-semibold text-sm px-4 py-2.5 rounded-lg shadow-sm transition-all">
            <span>{{ supportPhone }}</span>
          </a>
        </div>
      </div>

      <!-- Contact Form Card -->
      <div class="overflow-hidden bg-white rounded-xl border border-slate-200/80 shadow-sm">
        <form @submit.prevent="submit">
          <div class="p-8 space-y-6">
            
            <!-- User Information (Read Only) -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-5 rounded-xl border border-slate-100">
              <div>
                <span class="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{{ __('Votre nom') }}</span>
                <span class="text-sm font-medium text-slate-800">{{ user.name }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{{ __('Adresse e-mail') }}</span>
                <span class="text-sm font-medium text-slate-800">{{ user.email }}</span>
              </div>
              <div v-if="user.organization" class="md:col-span-2">
                <span class="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{{ __('Organisation / Commune') }}</span>
                <span class="text-sm font-medium text-slate-800">{{ user.organization }}</span>
              </div>
            </div>

            <hr class="border-slate-100" />

            <!-- Subject Input -->
            <div>
              <text-input 
                v-model="form.subject" 
                :error="form.errors.subject" 
                class="w-full" 
                :label="__('Sujet du problème')" 
                placeholder="Ex: Problème d'accès aux statistiques du tableau de bord"
              />
            </div>

            <!-- Message Input -->
            <div>
              <textarea-input 
                v-model="form.message" 
                :error="form.errors.message" 
                :rows="6"
                class="w-full" 
                :label="__('Description détaillée du problème')" 
                placeholder="Veuillez décrire le problème rencontré en fournissant le plus de détails possible..."
              />
            </div>

            <!-- Attachments Section -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="block text-sm font-medium text-slate-700">
                  {{ __('Pièces jointes') }}
                  <span class="text-xs text-slate-400 font-normal">({{ __('Optionnel - max 5 fichiers de 10 Mo chacun') }})</span>
                </label>
                <span v-if="form.attachments.length" class="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                  {{ form.attachments.length }} / 5 {{ __('fichiers') }}
                </span>
              </div>

              <!-- Drag & Drop / Upload Area -->
              <div 
                v-if="form.attachments.length < 5"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
                @click="triggerFileInput"
                :class="[
                  'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200',
                  isDragging 
                    ? 'border-indigo-500 bg-indigo-50/50 scale-[0.99]' 
                    : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50/60 bg-white'
                ]"
              >
                <input 
                  ref="fileInput" 
                  type="file" 
                  multiple 
                  accept=".jpg,.jpeg,.png,.gif,.svg,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar" 
                  class="hidden" 
                  @change="handleFileChange" 
                />

                <div class="flex flex-col items-center justify-center space-y-2">
                  <div class="p-3 bg-indigo-50 text-indigo-600 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-6 h-6">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                    </svg>
                  </div>
                  <div>
                    <span class="text-sm font-semibold text-indigo-600 hover:underline">
                      {{ __('Cliquez pour joindre un fichier') }}
                    </span>
                    <span class="text-sm text-slate-500"> {{ __('ou glissez-déposez ici') }}</span>
                  </div>
                  <p class="text-xs text-slate-400">
                    {{ __('Captures d\'écran, PDF, Word, Excel, texte ou archive (PNG, JPG, PDF, ZIP...)') }}
                  </p>
                </div>
              </div>

              <!-- Local Client Validation Error -->
              <div v-if="fileError" class="mt-2 text-xs text-red-600 flex items-center space-x-1 font-medium">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 shrink-0">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
                <span>{{ fileError }}</span>
              </div>

              <!-- Server Validation Errors -->
              <div v-if="form.errors.attachments" class="mt-2 text-xs text-red-600 font-medium">
                {{ form.errors.attachments }}
              </div>
              <div v-for="(err, key) in attachmentServerErrors" :key="key" class="mt-1 text-xs text-red-600 font-medium">
                {{ err }}
              </div>

              <!-- Attached Files List -->
              <div v-if="form.attachments.length" class="mt-4 space-y-2">
                <div 
                  v-for="(file, index) in form.attachments" 
                  :key="index"
                  class="flex items-center justify-between p-3 bg-slate-50 border border-slate-200/80 rounded-xl transition-all"
                >
                  <div class="flex items-center space-x-3 overflow-hidden min-w-0">
                    <!-- File Type Icon -->
                    <div class="p-2 rounded-lg shrink-0" :class="getFileIconBadgeClass(file)">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-5 h-5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                    </div>

                    <!-- File Info -->
                    <div class="truncate">
                      <p class="text-sm font-medium text-slate-800 truncate" :title="file.name">
                        {{ file.name }}
                      </p>
                      <p class="text-xs text-slate-400">
                        {{ formatFileSize(file.size) }}
                      </p>
                    </div>
                  </div>

                  <!-- Remove Button -->
                  <button 
                    type="button" 
                    @click.stop="removeAttachment(index)"
                    class="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-2 shrink-0"
                    :title="__('Supprimer ce fichier')"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

            </div>

          </div>

          <!-- Form Footer / Submit -->
          <div class="flex items-center justify-end px-8 py-4 border-t border-slate-100 bg-slate-50">
            <loading-button :loading="form.processing" class="btn-indigo" type="submit">
              {{ __('Envoyer le signalement') }}
            </loading-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    Link,
    TextInput,
    TextareaInput,
    LoadingButton,
  },
  layout: Layout,
  props: {
    supportPhone: String,
    user: Object,
  },
  remember: 'form',
  data() {
    return {
      isDragging: false,
      fileError: null,
      form: this.$inertia.form({
        subject: '',
        message: '',
        attachments: [],
      }),
    }
  },
  computed: {
    attachmentServerErrors() {
      const errors = []
      if (!this.form.errors) return errors
      for (const key in this.form.errors) {
        if (key.startsWith('attachments.')) {
          errors.push(this.form.errors[key])
        }
      }
      return errors
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileChange(e) {
      this.fileError = null
      const selected = Array.from(e.target.files || [])
      this.addFiles(selected)
      e.target.value = ''
    },
    handleDrop(e) {
      this.isDragging = false
      this.fileError = null
      const dropped = Array.from(e.dataTransfer.files || [])
      this.addFiles(dropped)
    },
    addFiles(files) {
      const maxFiles = 5
      const maxSizeBytes = 10 * 1024 * 1024 // 10 Mo

      if (this.form.attachments.length + files.length > maxFiles) {
        this.fileError = `Vous ne pouvez pas joindre plus de ${maxFiles} fichiers au total.`
      }

      for (const file of files) {
        if (this.form.attachments.length >= maxFiles) {
          break
        }
        if (file.size > maxSizeBytes) {
          this.fileError = `Le fichier "${file.name}" dépasse la taille maximale autorisée de 10 Mo.`
          continue
        }
        // Avoid duplicate by name and size
        const alreadyExists = this.form.attachments.some(
          att => att.name === file.name && att.size === file.size
        )
        if (!alreadyExists) {
          this.form.attachments.push(file)
        }
      }
    },
    removeAttachment(index) {
      this.form.attachments.splice(index, 1)
      this.fileError = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    formatFileSize(bytes) {
      if (!bytes || bytes <= 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'Ko', 'Mo', 'Go']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    getFileIconBadgeClass(file) {
      const type = (file.type || '').toLowerCase()
      const ext = (file.name || '').split('.').pop().toLowerCase()

      if (type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) {
        return 'bg-emerald-100 text-emerald-600'
      }
      if (type.includes('pdf') || ext === 'pdf') {
        return 'bg-red-100 text-red-600'
      }
      if (['doc', 'docx'].includes(ext) || type.includes('word')) {
        return 'bg-blue-100 text-blue-600'
      }
      if (['xls', 'xlsx', 'csv'].includes(ext) || type.includes('excel') || type.includes('spreadsheet')) {
        return 'bg-green-100 text-green-600'
      }
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
        return 'bg-amber-100 text-amber-600'
      }
      return 'bg-slate-100 text-slate-600'
    },
    submit() {
      this.fileError = null
      this.form.post(this.route('support.send-report'), {
        onSuccess: () => {
          this.form.reset()
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
        }
      })
    },
  },
}
</script>
